<script setup lang="ts">
import { onMounted, ref, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import {
  VyzvaPublicDetail,
  PrilohaResponse,
  DokumentResponse,
  VyzvaPublicDynamickaSekciaDynamickaSekciaKodEnum,
  VyzvaPublicDynamickaSekcia,
  MonitorovanyUkazovatelPublicResponseTypEnum,
  VyzvaPublicDetailStavEnum,
  VyzvaZmenyPoUzavretiResponse,
} from '/@src/client-public'
import { useHead } from '@vueuse/head'
import * as apis from '/@src/client-public/api'
import { toEuro } from '/@src/utils/numbers'
import { AxiosResponse } from 'axios'
import FileDownload from 'js-file-download'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import { useUserSession } from '/@src/stores/userSession'
import { useNotyf } from '/@src/composable/useNotyf'
import use404Redirect from '/@src/composable/use404Redirect'
import { isResourceNotFoundException } from '/@src/utils/errorHandling'
import schemaLibrary from '/@src/utils/zoppm/schemaLibrary'
import { detectIDKSComponentName } from '/@src/utils/zoppm/detectIDKSComponentName'
import { transformProps } from '/@src/utils/zoppm/transformProps'
import {
  formatDateString,
  formatUkazovatel,
  formatDateTimeString,
} from '/@src/utils/format'
import { checkPublicStateVyzva, checkPodavanieDateVyzva } from '/@src/utils/zoppm/misc'
import { formatPercentage } from '/@src/utils/moneyFormatter'

type Prilohy = Array<
  PrilohaResponse & {
    dokumenty: DokumentResponse[]
  }
>

const { t } = useI18n()
const route = useRoute()
const api = new apis.PublicVyzvaApi()
const userSession = useUserSession()

type SectionConfig = Readonly<{
  nazov: string
  hodnota: any
  podnadpis?: string
}>

const vyzva = ref<VyzvaPublicDetail>({})
const formalneNalezitostiVyzvyCast1 = reactive<SectionConfig[]>([])
const formalneNalezitostiVyzvyCast2 = reactive<SectionConfig[]>([])
const formalneNalezitostiVyzvyCast3 = reactive<SectionConfig[]>([])
const vyskaOpravnenychVydavkov = reactive<SectionConfig[]>([])
const obsahoveNalezitostiVyzvy = reactive<SectionConfig[]>([])
const podmienkyPoskytnutia = reactive<SectionConfig[]>([])
const informaciePreZiadatela = reactive<SectionConfig[]>([])
const prilohy = reactive<Prilohy>([])
const notyf = useNotyf()

const showVzorFo = ref<boolean>(true)
const showVzorPo = ref<boolean>(true)
const instrukcieFo = ref<string>('')
const instrukciePo = ref<string>('')
const nazovFo = ref<string>('')
const nazovPo = ref<string>('')

const plneZneniePrilohy = ref<any>([])
const plneZnenie = ref<any>(null)
const datumVsPredkladanie = ref({
  isBetween: false,
  isBefore: false,
  isAfter: false,
})
const isPublic = ref(false)

const podavanieButtonEnabled = computed(() => {
  const values = vyzva.value

  // Vyzva object was not set yet, button should be disabled.
  if (Object.keys(values).length === 0) {
    return false
  }

  datumVsPredkladanie.value = checkPodavanieDateVyzva(values)
  isPublic.value = checkPublicStateVyzva(values)

  return datumVsPredkladanie.value?.isBetween && isPublic.value
})

const isPaused = computed(() => {
  return vyzva.value?.stav === VyzvaPublicDetailStavEnum['Pozastavena']
})

const isClosed = computed(() => {
  return vyzva.value?.stav === VyzvaPublicDetailStavEnum['Uzatvorena']
})

const isCanceled = computed(() => {
  return vyzva.value?.stav === VyzvaPublicDetailStavEnum['Zrusena']
})

const dynoSpecProstriedkovSchemaRef = ref()
const dynoSpecVydavkovSchemaRef = ref()
const dynoDalsieInformacieSchemaRef = ref()

const { forceRedirectTo404 } = use404Redirect()

const createVyzvaDetailDynoSectionScheme = (data: VyzvaPublicDynamickaSekcia) => {
  if (!data) {
    return
  }

  const { nazov, dynamickaSekciaKod, value } = data
  const sectionChildrenConfigs = JSON.parse(value!)
  return {
    $el: 'div',
    children: sectionChildrenConfigs.map((config: any) => {
      const componentName = detectIDKSComponentName(config)
      if (!componentName) return undefined
      const props = transformProps(componentName, config)
      return {
        $cmp: 'DynamicSectionReadonly',
        props: {
          ...props,
          nazov,
          dynamickaSekciaKod,
          textSmall: data.dynamickaSekciaKod === 'VYZVA_DS_SPECPROSTRIEDKOV',
        },
      }
    }),
  }
}

const loadData = () => {
  api
    .getDetailPublicVyzva(<string>route.params.detailId, <string>route.query.previewToken)
    .then((values) => {
      if (values.data) {
        const dynoSpecProstriedkov = values.data.dynamickeSekcie?.find(
          (dynoSekcia) =>
            dynoSekcia.dynamickaSekciaKod ===
            VyzvaPublicDynamickaSekciaDynamickaSekciaKodEnum.Specprostriedkov
        )
        if (dynoSpecProstriedkov) {
          dynoSpecProstriedkovSchemaRef.value =
            createVyzvaDetailDynoSectionScheme(dynoSpecProstriedkov)
        }

        const dynoSpecVydavkov = values.data.dynamickeSekcie?.find(
          (dynoSekcia) =>
            dynoSekcia.dynamickaSekciaKod ===
            VyzvaPublicDynamickaSekciaDynamickaSekciaKodEnum.Specvydavkov
        )
        if (dynoSpecVydavkov) {
          dynoSpecVydavkovSchemaRef.value =
            createVyzvaDetailDynoSectionScheme(dynoSpecVydavkov)
        }

        const dynoDalsieInformacie = values.data.dynamickeSekcie?.find(
          (dynoSekcia) =>
            dynoSekcia.dynamickaSekciaKod ===
            VyzvaPublicDynamickaSekciaDynamickaSekciaKodEnum.Dalsieinformacie
        )
        if (dynoDalsieInformacie) {
          dynoDalsieInformacieSchemaRef.value =
            createVyzvaDetailDynoSectionScheme(dynoDalsieInformacie)
        }

        formalneNalezitostiVyzvyCast1.push(
          {
            nazov: 'vyzvy.status',
            hodnota: values.data.stav,
            class: 'mb-6',
          },
          {
            nazov: 'vyzvy.kodVyzvy',
            hodnota: values.data.kod,
            class: 'mb-6',
          },
          {
            nazov: 'vyzvy.datumVyhlaseniaVyzvy',
            hodnota: formatDateString(values.data.datumVyhlasenia),
          },
          {
            nazov: 'vyzvy.datumUzavretiaVyzvy',
            hodnota: formatDateTimeString(values.data.datumCasUzavretia),
          },
          {
            nazov: 'vyzvy.zoppmPodanieOd',
            hodnota: formatDateString(values.data.zoppmPodanieOd),
          },
          {
            nazov: 'vyzvy.zoppmPodanieDo',
            hodnota: formatDateString(values.data.zoppmPodanieDo),
            class: 'mb-6',
          },
          {
            nazov: 'vyzvy.uzavretieNaZakladeSkutocnosti',
            hodnota: values.data.uzavretieText,
          },
          {
            nazov: 'vyzvy.vyhlasovatelVyzvy',
            hodnota: values.data.vyhlasovatel,
          },
          {
            nazov: 'vyzvy.vykonavatelVyzvy',
            hodnota: values.data.vykonavatel,
          },
          {
            nazov: 'vyzvy.sprostredkovavatelVyzvy',
            hodnota: values.data.sprostredkovatel,
          },
          {
            nazov: 'vyzvy.sumVyskaProstriedkov',
            hodnota: toEuro(values.data.sumVyskaProstriedkov),
          }
        )
        formalneNalezitostiVyzvyCast2.push(
          {
            nazov: 'vyzvy.oblast',
            hodnota: values.data.oblast,
          },
          {
            nazov: 'vyzvy.komponent',
            hodnota: values.data.komponent,
          },
          {
            nazov: 'vyzvy.opatrenie',
            hodnota: values.data.opatrenie,
          }
        )
        formalneNalezitostiVyzvyCast3.push(
          {
            nazov: 'vyzvy.opravneniZiadatelia',
            hodnota: values.data.opravneniZiadatelia,
          },
          {
            nazov: 'vyzvy.cielVyzvy',
            hodnota: values.data.ciel,
          },
          {
            nazov: 'vyzvy.dovodyVyhlaseniaVyzvy',
            hodnota: values.data.vyhlasenaNaZaklade,
          },
          {
            nazov: 'vyzvy.miestoPodaniaZoPPM',
            hodnota: values.data.miestoPodaniaZoPPM,
          },
          {
            nazov: 'vyzvy.ineFormalneNalezitosti',
            hodnota: values.data.ineFormalneNalezitosti,
          }
        )

        if (values.data.sumOpravneneVydavkyMin) {
          vyskaOpravnenychVydavkov.push({
            nazov: 'vyzvy.sumOpravneneVydavkyMin',
            hodnota: toEuro(values.data.sumOpravneneVydavkyMin),
          })
        }
        if (values.data.sumOpravneneVydavkyMax) {
          vyskaOpravnenychVydavkov.push({
            nazov: 'vyzvy.sumOpravneneVydavkyMax',
            hodnota: toEuro(values.data.sumOpravneneVydavkyMax),
          })
        }
        if (values.data?.mieraSpolufinancovania) {
          vyskaOpravnenychVydavkov.push({
            nazov: 'vyzvy.mieraSpolufinancovania',
            hodnota: formatPercentage(values.data.mieraSpolufinancovania ?? 0),
          })
        }
        obsahoveNalezitostiVyzvy.push(
          {
            nazov: 'vyzvy.kriteriaHodnoteniaAIne',
            hodnota: values.data.kriteriaHodnoteniaZoPPM,
          },
          {
            nazov: 'vyzvy.sposobHodnoteniaZoPPM',
            hodnota: values.data.sposobHodnoteniaZoPPM,
          },
          {
            nazov: 'vyzvy.ineObsahoveNalezitosti',
            hodnota: values.data.ineObsahoveNalezitosti,
          }
        )
        podmienkyPoskytnutia.push(
          {
            nazov: 'vyzvy.podmienkyOpravnenostiZiadatela',
            hodnota: values.data.podmienkyOpravnenostiZiadatela,
          },
          {
            nazov: 'vyzvy.podmienkyDoruceniaZiadosti',
            hodnota: values.data.podmienkyDoruceniaZiadosti,
          },
          {
            nazov: 'vyzvy.podmienkyTykajuceSaStatnejPomoci',
            hodnota: values.data.podmienkyStatnejPomoci,
          },
          {
            nazov: 'vyzvy.dalsiePodmienkyZOsobitnychPredpisov',
            hodnota: values.data.dalsiePodmienkyZOsobitnychPredpisov,
          },
          {
            nazov: 'vyzvy.dalsiePodmienkyPoskytnutiaProstriedkov',
            hodnota: values.data.dalsiePodmienkyPoskytnutiaProstriedkov,
          },
          {
            nazov: 'vyzvy.ucelPouzitiaProstriedkov',
            hodnota: values.data.ucelPouzitiaProstriedkov,
          }
        )

        informaciePreZiadatela.push({
          nazov: 'vyzvy.informaciePreZiadatela',
          hodnota: values.data.dalsieNalezitostiVyzvy,
          podnadpis: 'vyzvy.dalsieNalezitostiVyzvy',
        })

        vyzva.value = values.data
        return values
      }
    })
    .then((vyzva) => {
      loadPrilohy(vyzva)
      loadPlneZnenie(vyzva)
    })
    .catch((err) => {
      if (isResourceNotFoundException(err)) {
        forceRedirectTo404()
      } else {
        notyf.error(t('globalne.chybaNacitaniaDat'))
      }
    })
}

const loadExampleInfo = async () => {
  await api
    .getVyzvaSpecificationPublicVyzva(
      <string>route.params.detailId,
      <string>route.query.previewToken
    )
    .then((response) => {
      response.data.zoppmSpec?.sekcieVyhodnotenePodmienky?.find((item) => {
        if (item.podmienka === 'ENABLED_ZoPPMSpec.vzor-ziadost-fo') {
          showVzorFo.value = <boolean>item.logickaHodnota
        }
      })

      response.data.zoppmSpec?.sekcieVyhodnotenePodmienky?.find((item) => {
        if (item.podmienka === 'ENABLED_ZoPPMSpec.vzor-ziadost-po') {
          showVzorPo.value = <boolean>item.logickaHodnota
        }
      })

      response.data.zoppmSpec?.sekcie?.find((sekcia) => {
        if (sekcia.sekciaRef?.nazov === 'zoppm.ss.vzor-ziadost-fo') {
          instrukcieFo.value = sekcia.instrukcie ? sekcia.instrukcie : ''
          nazovFo.value = sekcia.nadpis ? sekcia.nadpis : ''
        }
      })

      response.data.zoppmSpec?.sekcie?.find((sekcia) => {
        if (sekcia.sekciaRef?.nazov === 'zoppm.ss.vzor-ziadost-po') {
          instrukciePo.value = sekcia.instrukcie ? sekcia.instrukcie : ''
          nazovPo.value = sekcia.nadpis ? sekcia.nadpis : ''
        }
      })
    })
}

const loadPrilohy = async (vyzvaResponse?: AxiosResponse<VyzvaPublicDetail>) => {
  if (
    vyzvaResponse?.data.prilohaResponses &&
    vyzvaResponse.data.prilohaResponses?.length > 0 &&
    route.params.detailId
  ) {
    vyzvaResponse.data.prilohaResponses.forEach(async (prilohaResponse) => {
      if (prilohaResponse.poradie) {
        await api
          .findDokumentyForPrilohaOfVyzvaPublicVyzva(
            <string>route.params.detailId,
            prilohaResponse.poradie,
            <string>route.query.previewToken
          )
          .then(async (priloha) => {
            if (priloha.data.content) {
              prilohy.push({ ...prilohaResponse, dokumenty: priloha.data.content })
            }
          })
      }
    })
  }
}

const loadPlneZnenie = async (vyzvaResponse?: AxiosResponse<VyzvaPublicDetail>) => {
  if (vyzvaResponse?.data.plneZnenie) {
    plneZnenie.value = vyzvaResponse?.data.plneZnenie
    try {
      const prilohyPlne = await api.findDokumentyForPlneZnenieOfVyzvaPublicVyzva(
        <string>route.params.detailId,
        <string>route.query.previewToken
      )
      if (prilohyPlne?.data?.content) {
        plneZneniePrilohy.value = prilohyPlne.data?.content
      }
    } catch (error) {
      console.error(error)
    }
  }
}

function handleDownloadClick(dokumentId: number, cisloPrilohy: number) {
  api
    .fetchContentOfDokumentForPrilohaOfVyzvaPublicVyzva(
      <string>route.params.detailId,
      cisloPrilohy,
      <any>dokumentId,
      <string>route.query.previewToken,
      { responseType: 'blob' }
    )
    .then((response) => {
      FileDownload(response.data, response.headers.filename)
    })
}

function downloadPlneZnenieFile(dokumentId: number) {
  api
    .fetchContentOfDokumentForPlneZnenieOfVyzvaPublicVyzva(
      <string>route.params.detailId,
      <any>dokumentId,
      <string>route.query.previewToken,
      { responseType: 'blob' }
    )
    .then((response: any) => {
      FileDownload(response.data, response.headers.filename)
    })
    .catch((error: Error) => {
      console.error(error)
    })
}

useHead({
  title: t('globalne.title'),
})

const zmenyPoUzvretiResponse = ref<VyzvaZmenyPoUzavretiResponse>()
onMounted(async () => {
  await loadData()
  await loadExampleInfo()

  zmenyPoUzvretiResponse.value = await api.ziskajZmenyPoUzavretiPublicVyzva(
    route.params.detailId as string
  )
})
</script>

<template>
  <IDSKContainer>
    <IDSKBreadcrumbs
      :items="[
        { text: t('globalne.domov'), href: '/app/vyzvy' },
        { text: t('vyzvy.detailVyzvy') },
      ]"
      class="mb-8"
    />

    <IDSKParagraph class="mb-1" :variant="large">{{
      t('vyzvy.detailVyzvy')
    }}</IDSKParagraph>
    <IDSKHeading>{{ vyzva.nazov }}</IDSKHeading>

    <IDSKButtonGroup>
      <router-link
        v-if="userSession.isLoggedIn"
        v-slot="{ navigate }"
        :to="{
          path: `/app/vyzvy/${route.params.detailId}/zoppm`,
        }"
        custom
      >
        <IDSKButton
          :disabled="!podavanieButtonEnabled"
          data-test-id="vyzvy-detail-predlozit-zidaost"
          @click="navigate"
        >
          {{ t('vyzvy.predlozitZiadost') }}
        </IDSKButton>
      </router-link>
      <template v-else>
        <IDSKButton
          :disabled="!podavanieButtonEnabled"
          type="button"
          data-test-id="vyzvy-detail-vytvorit-ziadost-dialog"
          @click.prevent="authDialog?.show()"
        >
          {{ t('vyzvy.predlozitZiadost') }}
        </IDSKButton>
        <A11yDialog id="dialog-auth" @dialogRef="(d) => (authDialog = d)">
          <template #title>{{ t('vyzvy.neprihlasenyModalTitle') }}</template>
          <template #content>
            {{ t('vyzvy.neprihlasenyModalDesc') }}
          </template>
          <template #actions>
            <Prihlasenie />
          </template>
        </A11yDialog>
      </template>

      <!-- <router-link
        :to="{
          name: 'app-vyzvy-detailId-navod',
        }"
        custom
        v-slot="{ navigate }"
      >
        <IDSKLink href="#" @click="navigate" no-visited-state>{{
          t('vyzvy.akoPredlozitZiadost')
        }}</IDSKLink>
      </router-link> -->
      <router-link
        v-if="showVzorFo"
        v-slot="{ navigate }"
        :to="{
          name: 'app-vyzvy-detailId-ukazka-zoppm',
          query: { subjekt: 'FO', previewToken: route.query.previewToken },
        }"
        custom
      >
        <IDSKLink href="#" no-visited-state @click="navigate"
          >{{ nazovFo ? nazovFo : t('vyzvy.pozrietZiadostFO') }}
        </IDSKLink>
      </router-link>
      <router-link
        v-if="showVzorPo"
        v-slot="{ navigate }"
        :to="{
          name: 'app-vyzvy-detailId-ukazka-zoppm',
          query: { subjekt: 'PO', previewToken: route.query.previewToken },
        }"
        custom
      >
        <IDSKLink href="#" no-visited-state @click="navigate">{{
          nazovPo ? nazovPo : t('vyzvy.pozrietZiadostPO')
        }}</IDSKLink>
      </router-link>
    </IDSKButtonGroup>

    <IDSKWarningText v-if="isPublic && !datumVsPredkladanie.isBetween">
      <template v-if="datumVsPredkladanie.isBefore" #default>
        {{
          t('vyzvy.warningZiadostPredDatumomPredlozenia', {
            datum: formatDateString(vyzva?.zoppmPodanieOd),
          })
        }}
      </template>
      <template v-else-if="datumVsPredkladanie.isAfter" #default>
        {{
          t('vyzvy.warningZiadostPoDatumePredlozenia', {
            datum: formatDateString(vyzva?.zoppmPodanieDo),
          })
        }}<br />
        <i18n-t keypath="vyzvy.warningBackToVyzvy" :tag="false">
          <template #link>
            <IDSKRouterLink href="/app/vyzvy" no-visited-state>
              {{ t('vyzvy.warningBackToVyzvyLink') }}
            </IDSKRouterLink>
          </template>
        </i18n-t>
      </template>
    </IDSKWarningText>

    <IDSKWarningText v-if="isPaused || isClosed || isCanceled">
      <template v-if="isPaused" #default>
        {{ t('vyzvy.warningVyzvaPozastavena.main') }}<br />
        <i18n-t keypath="vyzvy.warningVyzvaPozastavena.ziadosti" :tag="false">
          <template #link>
            <IDSKRouterLink href="/app/ziadosti">
              {{ t('vyzvy.warningVyzvaPozastavena.ziadostiLink') }}
            </IDSKRouterLink>
          </template>
        </i18n-t>
        <br />
        <i18n-t keypath="vyzvy.warningVyzvaPozastavena.vyzvy" :tag="false">
          <template #link>
            <IDSKRouterLink href="/app/vyzvy">
              {{ t('vyzvy.warningVyzvaPozastavena.vyzvyLink') }}
            </IDSKRouterLink>
          </template>
        </i18n-t>
      </template>
      <template v-else-if="isCanceled || isClosed" #default>
        <template v-if="isClosed">
          {{ t('vyzvy.warningVyzvaUzatvorena') }}
        </template>
        <template v-else-if="isCanceled">
          {{ t('vyzvy.warningVyzvaZrusena') }}
        </template>
        <br />
        <i18n-t keypath="vyzvy.warningBackToVyzvy" :tag="false">
          <template #link>
            <IDSKRouterLink href="/app/vyzvy" no-visited-state>
              {{ t('vyzvy.warningBackToVyzvyLink') }}
            </IDSKRouterLink>
          </template>
        </i18n-t>
      </template>
    </IDSKWarningText>

    <section id="detail-vyzvy">
      <IDSKAccordion
        :id="'detail-vyzvy-prilohy'"
        :show-controls="false"
        :section-expanded="0"
      >
        <IDSKAccordionSection
          v-if="
            formalneNalezitostiVyzvyCast1.some((item) => item.hodnota) ||
            formalneNalezitostiVyzvyCast2.some((item) => item.hodnota) ||
            formalneNalezitostiVyzvyCast3.some((item) => item.hodnota) ||
            vyskaOpravnenychVydavkov.some((item) => item.hodnota) ||
            vyzva.mieraSpolufinancovaniaUpresnenie
          "
        >
          <template #heading>{{ t('vyzvy.formalneNalezitostiVyzvy') }}</template>

          <IDSKHeading as="h3" variant="s" color="blue">
            {{ t('vyzvy.zakladneUdaje') }}
          </IDSKHeading>

          <div class="govuk-body">
            <DisplayList
              :items-alingment="'stretch'"
              :class="[
                [dynoSpecProstriedkovSchemaRef ? 'mb-4' : 'mb-9'],
                'max-width--630',
              ]"
            >
              <template
                v-for="(polozka, index) in formalneNalezitostiVyzvyCast1"
                :key="polozka.nazov"
              >
                <template v-if="polozka && polozka.hodnota">
                  <dt class="mb-1">
                    <b>{{ t(polozka.nazov) }}</b>
                  </dt>

                  <dd :class="[polozka.class, { 'mb-2': !polozka.class }]">
                    <IDSKInvitationTag
                      v-if="index === 0 && polozka.hodnota"
                      :variant="polozka.hodnota"
                    />
                    <span v-else v-html="richTextToGovukHtml(polozka.hodnota)" />
                  </dd>
                </template>
              </template>
            </DisplayList>

            <template v-if="dynoSpecProstriedkovSchemaRef">
              <FormKitSchema
                class="max-width--630"
                :schema="dynoSpecProstriedkovSchemaRef"
                :library="schemaLibrary"
              />
            </template>

            <DisplayList class="mb-9 max-width--630">
              <template
                v-for="polozka in formalneNalezitostiVyzvyCast2"
                :key="polozka.nazov"
              >
                <template v-if="polozka.hodnota">
                  <dt class="mb-2">
                    <b>{{ t(polozka.nazov) }}</b>
                  </dt>

                  <dd class="mb-4">
                    <span v-html="richTextToGovukHtml(polozka.hodnota)" />
                  </dd>
                </template>
              </template>
            </DisplayList>

            <div>
              <template
                v-for="polozka in formalneNalezitostiVyzvyCast3"
                :key="polozka.nazov"
              >
                <template v-if="polozka.hodnota">
                  <hr class="mb-8" />
                  <IDSKHeading as="h3" variant="s" color="blue" class="mb-4">{{
                    t(polozka.nazov)
                  }}</IDSKHeading>
                  <div
                    class="mb-8 detail-vyzvy__obsah max-width--630"
                    v-html="richTextToGovukHtml(polozka.hodnota)"
                  />
                </template>
              </template>

              <template
                v-if="
                  vyskaOpravnenychVydavkov.length > 0 &&
                  vyskaOpravnenychVydavkov.some((item) => item.hodnota)
                "
              >
                <hr class="mb-8" />
                <IDSKHeading as="h3" variant="s" color="blue" class="mb-4">{{
                  t('vyzvy.vyskaOpravnenychVydavkov')
                }}</IDSKHeading>

                <ul class="mb-8 max-width--630">
                  <template v-for="polozka in vyskaOpravnenychVydavkov">
                    <li v-if="polozka.hodnota" :key="polozka.nazov">
                      <div class="d-flex align-items-justify">
                        <span>{{ t(polozka.nazov) }}</span>
                        <span>{{ polozka.hodnota }}</span>
                      </div>
                    </li>
                  </template>
                </ul>
              </template>

              <template v-if="vyzva.mieraSpolufinancovaniaUpresnenie">
                <hr class="mb-8" />
                <IDSKHeading
                  as="h3"
                  variant="s"
                  color="blue"
                  class="mb-4 max-width--630"
                  >{{ t('vyzvy.mieraSpolufinancovaniaUpresnenie') }}</IDSKHeading
                >

                <div
                  class="max-width--630"
                  v-html="richTextToGovukHtml(vyzva.mieraSpolufinancovaniaUpresnenie)"
                />
              </template>
              <template v-if="dynoSpecVydavkovSchemaRef">
                <IDSKDivider class="mb-9 mt-9" />
                <FormKitSchema
                  class="max-width--630"
                  :schema="dynoSpecVydavkovSchemaRef"
                  :library="schemaLibrary"
                />
              </template>

              <dl v-if="dynoDalsieInformacieSchemaRef">
                <IDSKDivider class="mb-9 mt-9" />
                <FormKitSchema
                  class="max-width--630"
                  :schema="dynoDalsieInformacieSchemaRef"
                  :library="schemaLibrary"
                />
              </dl>
            </div>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection
          v-if="obsahoveNalezitostiVyzvy.some((item) => item.hodnota)"
        >
          <template #heading>{{ t('vyzvy.obsahoveNalezitostiVyzvy') }} </template>

          <div class="govuk-body">
            <template
              v-for="(polozka, key) in obsahoveNalezitostiVyzvy"
              :key="polozka.nazov"
            >
              <template v-if="polozka.hodnota">
                <IDSKDivider v-if="key > 0" class="mt-8 mb-8" />
                <IDSKHeading
                  as="h3"
                  variant="s"
                  color="blue"
                  class="mb-1 max-width--630"
                  >{{ t(polozka.nazov) }}</IDSKHeading
                >
                <div
                  class="mb-8 detail-vyzvy__obsah max-width--630"
                  v-html="richTextToGovukHtml(polozka.hodnota)"
                />
              </template>
            </template>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection v-if="podmienkyPoskytnutia.some((item) => item.hodnota)">
          <template #heading>{{ t('vyzvy.podmienkyPoskytnutia') }}</template>

          <div class="govuk-body">
            <template
              v-for="(polozka, index) in podmienkyPoskytnutia"
              :key="polozka.nazov"
            >
              <template v-if="polozka.hodnota">
                <IDSKDivider v-if="index > 0" class="mt-8 mb-8" />
                <IDSKHeading as="h3" variant="s" color="blue" class="mb-4 max-width--630">
                  {{ t(polozka.nazov) }}
                </IDSKHeading>
                <IDSKHeading
                  v-if="polozka.podnadpis"
                  as="h4"
                  variant="m"
                  class="mb-4 max-width--630"
                >
                  {{ t(polozka.podnadpis) }}
                </IDSKHeading>
                <div
                  class="mb-4 detail-vyzvy__obsah max-width--630"
                  v-html="richTextToGovukHtml(polozka.hodnota)"
                />
              </template>
            </template>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection
          v-if="vyzva.ukazovatelList && vyzva.ukazovatelList.length > 0"
          class="detail-vyzvy__merne-jednotky"
        >
          <template #heading>{{ t('vyzvy.monitorovacieUkazovatele') }}</template>

          <div class="govuk-body">
            <div
              v-for="(ukazovatel, index) in vyzva.ukazovatelList"
              :key="ukazovatel.nazov"
              class="mb-8"
            >
              <IDSKDivider v-if="index > 0" class="mt-8 mb-8" />
              <IDSKHeading as="h4" variant="s" color="blue">{{
                ukazovatel.nazov
              }}</IDSKHeading>
              <IDSKParagraph>{{ ukazovatel.popis }}</IDSKParagraph>

              <ul>
                <li class="mb-3">
                  <b>{{ t('globalne.typ') }}: </b> {{ formatUkazovatel(ukazovatel.typ) }}
                </li>
                <li
                  v-if="
                    ukazovatel.typ !==
                    MonitorovanyUkazovatelPublicResponseTypEnum.Kvalitativny
                  "
                >
                  <b>{{ t('vyzvy.mernaJednotka') }}: </b>
                  {{ ukazovatel.mernaJednotka || '-' }}
                </li>
              </ul>
            </div>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection v-if="informaciePreZiadatela.some((item) => item.hodnota)">
          <template #heading>{{ t('vyzvy.informaciePreZiadatela') }}</template>

          <div class="govuk-body">
            <template
              v-for="(informacie, key) in informaciePreZiadatela"
              :key="informacie.nazov"
            >
              <template v-if="informacie.hodnota">
                <IDSKDivider v-if="key > 0" class="mt-8 mb-8" />
                <IDSKHeading as="h3" variant="s" color="blue" class="mb-4 max-width--630">
                  {{ t(informacie.podnadpis) }}
                </IDSKHeading>
                <div
                  class="mb-8 detail-vyzvy__obsah max-width--630"
                  v-html="richTextToGovukHtml(informacie.hodnota)"
                />
              </template>
            </template>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection v-if="vyzva.stav === 'UZATVORENA'">
          <template #heading>Zmeny výzvy po uzatvorení</template>
          <div class="govuk-body">
            <div id="priklad-1" data-module="idsk-table">
              <table class="idsk-table">
                <thead class="idsk-table__head">
                  <tr class="idsk-table__row">
                    <th scope="col" class="idsk-table__header">
                      <span class="th-span"> Zmena</span>
                    </th>
                    <th scope="col" class="idsk-table__header">
                      <span class="th-span"> Popis </span>
                    </th>
                  </tr>
                </thead>
                <tbody class="idsk-table__body" style="">
                  <tr
                    v-for="(zmenaPoUzvreti, index) in zmenyPoUzvretiResponse?.data
                      ?.zmenaPoUzvreti"
                    :key="index"
                    class="idsk-table__row"
                  >
                    <td class="idsk-table__cell">
                      {{ formatDateTimeString(zmenaPoUzvreti.kedyZmenil) }}
                    </td>
                    <td class="idsk-table__cell">
                      <tenplate v-if="zmenaPoUzvreti?.datumCasUzavretia">
                        Dátum uzavretia:
                        {{ formatDateTimeString(zmenaPoUzvreti.datumCasUzavretia) }}
                        <br />
                      </tenplate>
                      <template v-if="zmenaPoUzvreti?.datumUzavretiaPredpokladany">
                        Predpokladaný dátum uzavretia:
                        {{ formatDateString(zmenaPoUzvreti.datumUzavretiaPredpokladany) }}
                        <br />
                      </template>
                      <template v-if="zmenaPoUzvreti?.sumZostatokProstriedkov">
                        Zostatok Finančných prostriedkov:
                        {{ zmenaPoUzvreti.sumZostatokProstriedkov }}
                        <br />
                      </template>
                      <template v-if="zmenaPoUzvreti?.sumVyskaProstriedkov">
                        Indikatívna výška prostriedkov mechanizmu určených na výzvu:
                        {{ zmenaPoUzvreti?.sumVyskaProstriedkov }}
                      </template>
                      <template v-if="zmenaPoUzvreti?.plneZnenie">
                        {{ zmenaPoUzvreti?.plneZnenie }}
                      </template>
                      <template v-if="zmenaPoUzvreti?.priloha">
                        {{ zmenaPoUzvreti?.priloha }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection v-if="plneZnenie">
          <template #heading>{{ t('globalne.plneZnenieHeading') }}</template>

          <div class="govuk-body">
            <IDSKHeading variant="s" color="blue" as="h4">{{
              plneZnenie?.nazov
            }}</IDSKHeading>
            <p class="max-width--630">{{ plneZnenie?.poznamka }}</p>
            <IDSKTable
              v-if="plneZneniePrilohy.length > 0"
              :id="`detail-vyzvy-plne-znenie-files-tabulka`"
              :columns="{
                nazov: t('globalne.subory'),
                dokumentId: '',
              }"
              :data="plneZneniePrilohy"
            >
              <template #cell(nazov)="{ value }">
                {{ value }}
              </template>
              <template #cell(dokumentId)="{ value }">
                <div class="d-flex align-items-right">
                  <IDSKLink
                    href="#"
                    download
                    class="mb-0"
                    variant="secondary"
                    @click.prevent="() => downloadPlneZnenieFile(value)"
                  >
                    {{
                      plneZneniePrilohy.find(({ dokumentId }) => dokumentId === value)
                        ?.originalFileName
                    }}
                  </IDSKLink>
                </div>
              </template>
            </IDSKTable>
            <IDSKParagraph v-else>
              {{ t('globalne.bazPrilohy') }}
            </IDSKParagraph>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection v-if="prilohy[0]">
          <template #heading>{{ t('globalne.prilohy') }}</template>

          <div class="govuk-body">
            <template v-for="(priloha, index) in prilohy" :key="priloha.nazov">
              <IDSKDivider v-if="index > 0" class="mt-8 mb-8" />
              <IDSKHeading variant="s" color="blue" as="h4"
                >{{ index + 1 }}. {{ priloha.nazov }}</IDSKHeading
              >
              <p class="max-width--630">{{ priloha.poznamka }}</p>
              <IDSKTable
                :id="`detail-vyzvy-prilohy-tabulka-${index}`"
                :columns="{
                  nazov: t('globalne.subory'),
                  dokumentId: '',
                }"
                :data="priloha.dokumenty"
              >
                <template #cell(nazov)="{ value }">
                  {{ value }}
                </template>
                <template #cell(dokumentId)="{ value }">
                  <div class="d-flex align-items-right">
                    <IDSKLink
                      href="#"
                      download
                      class="mb-0"
                      variant="secondary"
                      @click.prevent="
                        () =>
                          priloha.poradie && handleDownloadClick(value, priloha.poradie)
                      "
                    >
                      {{
                        priloha.dokumenty.find(({ dokumentId }) => dokumentId === value)
                          ?.originalFileName
                      }}
                    </IDSKLink>
                  </div>
                </template>
              </IDSKTable>
            </template>
          </div>
        </IDSKAccordionSection>

        <IDSKAccordionSection
          v-if="
            vyzva.sposobKomunikacie ||
            vyzva.vykonavatelAdresa?.ulica ||
            vyzva.sprostredkovatelAdresa?.ulica
          "
        >
          <template #heading>{{ t('vyzvy.kontaktneUdaje') }}</template>

          <div class="govuk-body">
            <template v-if="vyzva.sposobKomunikacie">
              <IDSKHeading as="h3" variant="s" color="blue" class="mb-4 max-width--630">{{
                t('vyzvy.sposobKomunikacie')
              }}</IDSKHeading>
              <p
                class="mb-2 detail-vyzvy__obsah max-width--630 sposob-text-format"
                v-html="richTextToGovukHtml(vyzva.sposobKomunikacie)"
              />
            </template>

            <template v-if="vyzva.vykonavatelAdresa?.ulica">
              <IDSKDivider v-if="vyzva.sposobKomunikacie" class="mt-8 mb-8" />
              <IDSKHeading as="h3" variant="s" color="blue" class="mb-4">{{
                t('vyzvy.vykonavatel')
              }}</IDSKHeading>
              <p class="mb-2">{{ vyzva.vykonavatel }}</p>
              <p class="mb-2">
                {{ t('globalne.adresa') }}: {{ vyzva.vykonavatelAdresa?.ulica }}
                {{
                  vyzva.vykonavatelAdresa?.orientacneCislo
                    ? `${vyzva.vykonavatelAdresa?.supisneCislo}/${vyzva.vykonavatelAdresa?.orientacneCislo}`
                    : vyzva.vykonavatelAdresa?.supisneCislo
                }},
                {{ vyzva.vykonavatelAdresa?.psc }}
                {{ vyzva.vykonavatelAdresa?.obec?.nazov }}
              </p>
              <p class="mb-5">
                {{ t('globalne.email') }}: {{ vyzva.vykonavatelKontakt?.email }}
              </p>
            </template>
            <template
              v-for="(sprostredkovatel, index) in vyzva?.sprostredkovatelia"
              :key="sprostredkovatel.sprostredkovatel"
            >
              <IDSKDivider v-if="sprostredkovatel.sprostredkovatel" class="mt-8 mb-8" />
              <IDSKHeading as="h3" variant="s" color="blue" class="mb-4">{{
                t('vyzvy.sprostredkovatel') + ' č. ' + (index + 1)
              }}</IDSKHeading>
              <p class="mb-2">{{ sprostredkovatel.sprostredkovatel }}</p>
              <p class="mb-2">
                {{ t('globalne.adresa') }}:
                {{ sprostredkovatel.sprostredkovatelAdresa?.ulica }}
                {{
                  sprostredkovatel.sprostredkovatelAdresa?.orientacneCislo
                    ? `${sprostredkovatel.sprostredkovatelAdresa?.supisneCislo}/${sprostredkovatel.sprostredkovatelAdresa?.orientacneCislo}`
                    : sprostredkovatel.sprostredkovatelAdresa?.supisneCislo
                }},
                {{ sprostredkovatel.sprostredkovatelAdresa?.psc }}
                {{ sprostredkovatel.sprostredkovatelAdresa?.obec?.nazov }}
              </p>
              <p class="mb-6">
                {{ t('globalne.email') }}:
                {{ sprostredkovatel.sprostredkovatelKontakt?.email }}
              </p>
            </template>
          </div>
        </IDSKAccordionSection>
      </IDSKAccordion>
    </section>
  </IDSKContainer>
</template>

<style lang="scss" scoped>
#detail-vyzvy {
  & dl dd {
    margin-left: 0;

    & > *:last-child {
      margin-bottom: 0;
    }
  }

  & .detail-vyzvy__merne-jednotky ul {
    padding: 0;
    list-style-type: none;
  }

  & .detail-vyzvy__obsah > *:last-child {
    margin-bottom: 0;
  }
}

#detail-vyzvy-subtitle {
  color: var(--fade-grey);
}

.sposob-text-format {
  line-height: 29px;
}
</style>
